import React from 'react';
/**
 * Keeps a component mounted while it animates in and out, so a CSS leave
 * animation can play before the element is removed from the DOM. This is a
 * dependency-free replacement for `react-spring`'s `useTransition` for the
 * common "show/hide a single element" case.
 *
 * Render the element while `shouldRender` is `true` and drive a CSS transition
 * from the returned `isVisible` flag (e.g. toggle opacity/transform/height).
 *
 * The element does not animate on its initial mount: `isVisible` is seeded from
 * `isOpen`, so an element that starts open simply appears.
 *
 * @param isOpen - Whether the element should currently be shown.
 * @param durationMs - Duration of the leave transition in milliseconds. The
 *   element is unmounted only after this delay, so it must match the CSS
 *   transition duration for the animation to complete cleanly.
 * @returns `shouldRender` (keep the element mounted) and `isVisible` (the CSS
 *   target state to transition towards).
 *
 * @example
 * ```tsx
 * const {shouldRender, isVisible} = useMountTransition(isOpen, 200);
 * return shouldRender ? <FadingBox isVisible={isVisible}>{children}</FadingBox> : null;
 * ```
 */
export const useMountTransition = (isOpen, durationMs) => {
    const [shouldRender, setShouldRender] = React.useState(isOpen);
    const [isVisible, setIsVisible] = React.useState(isOpen);
    React.useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            // Two animation frames guarantee the browser paints the element in its
            // initial (hidden) state before we flip to visible, so the CSS
            // transition actually runs instead of snapping straight to the end.
            let innerFrame = 0;
            const outerFrame = requestAnimationFrame(() => {
                innerFrame = requestAnimationFrame(() => setIsVisible(true));
            });
            return () => {
                cancelAnimationFrame(outerFrame);
                cancelAnimationFrame(innerFrame);
            };
        }
        setIsVisible(false);
        const timeoutId = window.setTimeout(() => setShouldRender(false), durationMs);
        return () => window.clearTimeout(timeoutId);
    }, [isOpen, durationMs]);
    return { shouldRender, isVisible };
};
